import { ColumnFilterItem } from 'antd/lib/table/interface'
import { CIMSColumnFilterItem } from '../../../../CIMSColumn/types/filter'
import { useCallback } from 'react'
import { Tooltip } from 'antd'

/**
 * **ONLY FOR USE IN THE CIMS TABLE**
 *
 * converts {@link CIMSColumnFilterItem} items into {@link ColumnFilterItem} items
 *
 * this also removes duplicates where the {@link CIMSColumnFilterItem.value|value}
 * is the same
 */
export function useColumnFilterItems() {
  const toCIMSColumnFilterItem: (
    item: string | number | CIMSColumnFilterItem
  ) => CIMSColumnFilterItem = useCallback((item) => {
    if (typeof item === 'string' || typeof item === 'number')
      return { text: item.toString(), value: item }
    return item
  }, [])

  const makeUnique: (
    items: (string | number | CIMSColumnFilterItem | undefined | null)[]
  ) => CIMSColumnFilterItem[] = useCallback(
    (items) => {
      const unique = new Map<
        CIMSColumnFilterItem['value'],
        CIMSColumnFilterItem
      >()
      items.forEach((item) => {
        if (item === undefined || item === null) return
        // resolve the current item
        const { text, value, children, tooltip } = toCIMSColumnFilterItem(item)

        const existing = unique.get(value)

        if (!existing) {
          // if we don't have this item yet, add it
          const newItem: CIMSColumnFilterItem = { text, value, tooltip }
          if (children) newItem.children = makeUnique(children)
          unique.set(value, newItem)
        } else if (children) {
          // if we have the item, it has children, and there's additional children
          // in the current iteration of the item, add those to it's children,
          // making unique as we go down
          const existingChildren = existing.children ?? []
          existing.children = makeUnique(existingChildren.concat(children))
          unique.set(value, existing)
        }
      })
      return [...unique.values()]
    },
    [toCIMSColumnFilterItem]
  )

  const withTooltip: (
    items: CIMSColumnFilterItem[] | undefined
  ) => ColumnFilterItem[] | undefined = useCallback(
    (items) =>
      items?.map<ColumnFilterItem>(
        ({ text: label, value, children, tooltip }) => {
          return {
            text: tooltip ? <Tooltip title={tooltip}>{label}</Tooltip> : label,
            value,
            children: withTooltip(children),
          }
        }
      ),
    []
  )

  const callback = useCallback(
    (items: (string | number | CIMSColumnFilterItem)[] | undefined) => {
      if (!items) return []
      return withTooltip(makeUnique(items))
    },
    [makeUnique, withTooltip]
  )
  return callback
}
